import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { companyInfoService, faqService } from '../services/faqService';
import { CompanyInfoInput, FaqEntryInput } from '../types';

export const faqKeys = {
  all: ['knowledge-base', 'faq'] as const,
  list: (knowledgeBaseId: string, search: string) =>
    ['knowledge-base', 'faq', knowledgeBaseId, search] as const,
  companyInfo: (knowledgeBaseId: string) =>
    ['knowledge-base', 'company-info', knowledgeBaseId] as const,
};

// Polls while an entry is being embedded, so its status updates without a reload
const PROCESSING_POLL_INTERVAL_MS = 3000;

export const useFaqEntries = (knowledgeBaseId: string, search: string) =>
  useQuery({
    queryKey: faqKeys.list(knowledgeBaseId, search),
    queryFn: () => faqService.list(knowledgeBaseId, search),
    placeholderData: keepPreviousData,
    refetchInterval: query =>
      query.state.data?.some(entry => entry.status === 'processing')
        ? PROCESSING_POLL_INTERVAL_MS
        : false,
  });

const useInvalidateFaq = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: faqKeys.all });
};

export const useCreateFaqEntry = (knowledgeBaseId: string) => {
  const invalidate = useInvalidateFaq();
  return useMutation({
    mutationFn: (data: FaqEntryInput) => faqService.create(knowledgeBaseId, data),
    onSuccess: invalidate,
  });
};

export const useUpdateFaqEntry = () => {
  const invalidate = useInvalidateFaq();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: FaqEntryInput }) => faqService.update(id, data),
    onSuccess: invalidate,
  });
};

export const useDeleteFaqEntry = () => {
  const invalidate = useInvalidateFaq();
  return useMutation({
    mutationFn: (id: string) => faqService.remove(id),
    onSuccess: invalidate,
  });
};

export const useCompanyInfo = (knowledgeBaseId: string) =>
  useQuery({
    queryKey: faqKeys.companyInfo(knowledgeBaseId),
    queryFn: () => companyInfoService.get(knowledgeBaseId),
    refetchInterval: query =>
      query.state.data?.some(entry => entry.status === 'processing')
        ? PROCESSING_POLL_INTERVAL_MS
        : false,
  });

export const useSaveCompanyInfo = (knowledgeBaseId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CompanyInfoInput) => companyInfoService.save(knowledgeBaseId, data),
    onSuccess: entries => queryClient.setQueryData(faqKeys.companyInfo(knowledgeBaseId), entries),
  });
};

export const useProcessFaqEntry = () => {
  const invalidate = useInvalidateFaq();
  return useMutation({
    mutationFn: (id: string) => faqService.process(id),
    onSuccess: invalidate,
  });
};
